import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, Match, Show, Switch } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { LinearIcon } from "./linearIcon";
import { type LinearSetupRequiredPayload, parseLinearSetupRequiredPayload } from "./linearSetupRequiredSchema";

interface LinearSetupRequiredRendererProps {
  block: StreamingBlock;
}

const REASON_BODY_KEYS: Record<LinearSetupRequiredPayload["reason"], string> = {
  workspace_not_configured: "linear_setup_card.body_workspace_not_configured",
  not_exposed: "linear_setup_card.body_not_exposed",
  auth_broken: "linear_setup_card.body_auth_broken",
};

export const LinearSetupRequiredRenderer = (props: LinearSetupRequiredRendererProps) => {
  const rt = useChatRuntime();

  const isOrgAdmin = () => rt.ui?.isOrgAdmin?.() ?? false;
  const settingsHref = () => rt.actions.integrationSettingsHref?.("linear");

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearSetupRequiredPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearSetupRequiredPayload(json);
  });

  return (
    <Show
      when={payload()}
      fallback={
        <Show when={rawContent()}>
          <div class="rounded-lg border border-primary-border bg-panel p-4">
            <p class="text-xs text-muted-text">{rt.t("linear_setup_card.parse_error")}</p>
          </div>
        </Show>
      }
    >
      {(data) => (
        <div class="rounded-lg border border-primary-border bg-panel p-4">
          <div class="flex items-start gap-3">
            <LinearIcon class="size-6 shrink-0 text-primary-text" />
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-medium text-primary-text">{rt.t("linear_setup_card.title")}</h3>
              <p class="mt-1 text-xs text-muted-text">{rt.t(REASON_BODY_KEYS[data().reason])}</p>
            </div>
          </div>
          <Switch fallback={<p class="mt-3 text-xs text-muted-text">{rt.t("linear_setup_card.non_admin_note")}</p>}>
            <Match when={isOrgAdmin() && settingsHref()}>
              <a
                href={settingsHref()}
                class="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
              >
                <span>{rt.t("linear_setup_card.admin_cta")}</span>
                <TbExternalLink class="size-4" />
              </a>
            </Match>
            {/* Chief opens settings in the system browser rather than at an
                href, so it gets the same CTA as a button. */}
            <Match when={rt.actions.connectProvider}>
              <button
                type="button"
                onClick={() => {
                  rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "linear", via: "button" });
                  rt.actions.connectProvider?.("linear");
                }}
                class="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
              >
                <span>{rt.t("linear_setup_card.admin_cta")}</span>
                <TbExternalLink class="size-4" />
              </button>
            </Match>
          </Switch>
        </div>
      )}
    </Show>
  );
};
